import type { AudienceType, CampaignAudienceSegment, Production, RuleGroup } from '@/api/types';
import {
  audienceTypeFromRules,
  originalRules,
  resolveVisualAudienceRules,
  visualGroupsFromCanonical,
  visualTypeFromAudienceType,
  type AudienceRuleResolutionContext,
  type VisualAudienceGroup,
  type VisualAudienceType,
} from './audience-visual-rules';
import {
  resolveCampaignAudienceDefinition,
  type AudienceRevisionDefinition,
  type CurrentAudienceDefinition,
} from './campaign-audience-revisions';

// A campaign's audiences, one slot each.
//
// The persisted model has always been plural — `campaign_audience_segments` is
// N audiences with allocations, each becoming its own ad set / ad group and its
// own reportable delivery group — but the wizard held ONE audience type for the
// whole campaign beside a list of groups. That asymmetry was the only thing
// stopping a campaign mixing a direct audience with a lookalike.
//
// One slot maps to exactly one of everything downstream:
//
//   slot i == audience_segments[i] == platform_delivery_groups[position=i]
//          == SAM_API_V1__<campaign>__R<rev>__AUD<i>
//          == reporting `audience_segment_position` = i
//
// Keeping that one-to-one is what lets reporting slice a mixed campaign by
// audience type with no schema change at all.

export type { VisualAudienceType };

/** What hydration reads from a saved plan (`CampaignPlan` satisfies it). */
export interface AudienceSlotPlan {
  audience_segments?: readonly Pick<CampaignAudienceSegment, 'audience_id' | 'audience_revision' | 'position'>[];
  audience_revisions?: readonly AudienceRevisionDefinition[];
}

/** Builds the starting branches for a slot of one type. */
export type DefaultAudienceGroups = (
  type: VisualAudienceType,
  context: AudienceRuleResolutionContext,
) => VisualAudienceGroup[];

export interface CampaignAudienceSlot {
  /** Stable identity; becomes the pinned audience id once materialized. */
  id: string;
  /** Selects this slot's rule vocabulary. One audience, one type. */
  type: VisualAudienceType;
  /** OR branches WITHIN this one audience. Exactly one for a Lookalike. */
  groups: VisualAudienceGroup[];
}

export interface HydratedAudienceSlots {
  slots: CampaignAudienceSlot[];
  /**
   * Segment positions whose pinned definition could not be resolved.
   *
   * Never silently dropped. The previous rehydration `continue`d past these,
   * so the next save wrote FEWER segments than the plan pinned — orphaning a
   * live provider ad set and truncating its reporting. The caller refuses to
   * save while this is non-empty.
   */
  unresolvedPositions: number[];
}

/**
 * Rebuild the wizard's audience state from a saved plan, losslessly.
 *
 * Three separate ways to destroy a plan used to live here, none of which could
 * bite while a campaign could only hold one audience type:
 *
 *  1. the type was read from segment 0 and stamped on every segment;
 *  2. each decoded OR branch was pushed as a top-level group, so a saved
 *     audience with two branches became two audiences on the next save;
 *  3. an unresolvable segment was skipped entirely.
 *
 * One slot per segment, in position order, fixes the first two by construction
 * and reports the third.
 */
export function audienceSlotsFromPlan(
  plan: AudienceSlotPlan | null | undefined,
  audiences: readonly CurrentAudienceDefinition[] = [],
  productions: readonly Pick<Production, 'id' | 'name'>[] = [],
  defaultGroups: DefaultAudienceGroups,
): HydratedAudienceSlots {
  const segments = [...(plan?.audience_segments ?? [])]
    .sort((left, right) => left.position - right.position);

  if (segments.length === 0) {
    return {
      slots: [{
        id: 'segment-1',
        type: 'Direct',
        groups: defaultGroups('Direct', { audiences, productions }),
      }],
      unresolvedPositions: [],
    };
  }

  const slots: CampaignAudienceSlot[] = [];
  const unresolvedPositions: number[] = [];
  for (const segment of segments) {
    const definition = resolveCampaignAudienceDefinition(
      segment,
      plan?.audience_revisions,
      audiences,
    );
    if (!definition) {
      unresolvedPositions.push(segment.position);
      continue;
    }
    slots.push({
      id: definition.id,
      // Per slot. Reading segment 0's type and applying it to the rest is the
      // bug this whole function exists to close.
      type: visualTypeFromAudienceType(definition.audience_type),
      // Every branch stays INSIDE this slot: they are OR branches of one
      // audience, not separate audiences.
      groups: visualGroupsFromCanonical(definition.rules),
    });
  }

  return { slots, unresolvedPositions };
}

/**
 * The canonical rules to persist for one slot.
 *
 * A single-branch slot must emit the BARE `all` group, byte for byte what the
 * wizard has always posted. `materializeAudienceSegments` decides whether to
 * reuse an existing campaign-owned audience by comparing
 * `JSON.stringify(existing.rules)`, so emitting `any([all])` instead would make
 * every already-saved campaign audience look edited and fork a duplicate on the
 * first save after this ships.
 *
 * `write_segments` in the shared validator accepts both shapes precisely because
 * the wizard emits the flat one.
 */
export function slotRules(
  groups: readonly VisualAudienceGroup[],
  context: AudienceRuleResolutionContext,
): RuleGroup {
  const encoded = resolveVisualAudienceRules(groups, context);
  const only = encoded.segments.length === 1 ? encoded.segments[0] : undefined;
  return only ? only.rules : encoded.rules;
}

/**
 * Whether a slot's audience can be reused as the saved audience `existing`.
 *
 * A slot still holding the very rows decoded from a saved audience matches that audience's
 * stored tree as it is, whatever its shape (a bare `all`, an `any` of unwrapped leaves, a
 * hand-made age range): its canonical re-encoding would read as an edit and fork a copy on
 * every save. Any slot also matches an audience holding its canonical `rules`, as it always did.
 */
export function slotMatchesSavedAudience(
  groups: readonly VisualAudienceGroup[],
  rules: RuleGroup,
  audienceType: AudienceType,
  existing: { audience_type: AudienceType; rules: RuleGroup },
): boolean {
  // The canonical rules match whenever the slot was edited, and also once a save created
  // the canonical copy of an untouched slot: that copy must be reused on the next save and on
  // a retry, while the rows still remember the tree they were decoded from.
  if (existing.audience_type === audienceType && JSON.stringify(existing.rules) === JSON.stringify(rules)) {
    return true;
  }
  const untouched = originalRules(groups);
  return !!untouched
    && existing.audience_type === audienceTypeFromRules(untouched)
    && JSON.stringify(existing.rules) === JSON.stringify(untouched);
}

/**
 * Everything the save fingerprint needs from the audience section.
 *
 * Ids are deliberately excluded. Materialization rewrites `slot.id` to the
 * created audience id, so including it would mark the form dirty the instant a
 * save succeeded — and a dirty published campaign writes a new revision, new
 * delivery groups and new provider objects for an edit nobody made.
 */
export function audienceSlotsFingerprint(slots: readonly CampaignAudienceSlot[]): unknown {
  return slots.map((slot) => ({
    type: slot.type,
    groups: slot.groups.map((group) => ({ op: group.op, rules: group.rules })),
  }));
}

/**
 * Has every slot been confirmed?
 *
 * The wizard advances out of the Audience section only when this flips true, so
 * confirming one of three leaves the section open on the other two. Extracted
 * rather than inlined in the builder: a predicate that decides whether the user
 * can proceed should be reachable by a unit test.
 */
export function allAudienceSlotsConfirmed(
  slots: readonly CampaignAudienceSlot[],
  confirmed: Readonly<Record<string, boolean>>,
): boolean {
  return slots.length > 0 && slots.every((slot) => !!confirmed[slot.id]);
}

/**
 * Move a value from one slot id to another, for maps keyed by slot id.
 *
 * Saving materializes each slot into a real audience and renames the slot to
 * that audience's id (`materializeAudienceSegments`, create-flow-save.ts). Two
 * side maps are keyed by the OLD id — `slotConfirmed` and `slotPreviews` — and
 * a rename without this leaves both orphaned: the section silently un-ticks
 * itself on first save, and a lone lookalike loses the size it just computed.
 *
 * A no-op when the key is absent or unchanged, so it is safe to call
 * unconditionally.
 */
export function rekeyBySlotId<T>(
  map: Readonly<Record<string, T>>,
  previousId: string,
  nextId: string,
): Readonly<Record<string, T>> {
  if (previousId === nextId || !(previousId in map)) return map;
  // Rebuilt by filtering rather than by rest-destructuring: the rest element of
  // an index-signature type widens every value to `T | undefined`, which would
  // make callers handle an absence this function cannot produce.
  const out: Record<string, T> = {};
  for (const [key, value] of Object.entries(map)) {
    if (key !== previousId) out[key] = value;
  }
  out[nextId] = map[previousId]!;
  return out;
}
